import React, { useEffect, useMemo, useState } from 'react';
import {
	ResponsiveContainer,
	LineChart,
	Line,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
} from 'recharts';

const SERIES_COLORS = ['#4F6FC4', '#556287', '#8DB7E8', '#D97706', '#0F766E', '#B91C1C'];

function normalizeDataPoint(item, index) {
	if (item && typeof item === 'object') {
		const nameRaw = item.name;
		const valueRaw = item.value;
		const seriesRaw = item.series;

		const name = typeof nameRaw === 'string' && nameRaw.trim() ? nameRaw : `Row ${index + 1}`;
		const value = typeof valueRaw === 'number' ? valueRaw : Number(valueRaw) || 0;
		const series = typeof seriesRaw === 'string' ? seriesRaw : null;

		return { name, value, series };
	}

	if (typeof item === 'number') {
		return { name: `Row ${index + 1}`, value: item, series: null };
	}

	return { name: `Row ${index + 1}`, value: 0, series: null };
}

function LineChartWidget({ data, showSeries = false }) {
	const sourceRows = useMemo(() => {
		if (Array.isArray(data)) {
			return data;
		}

		if (data && typeof data === 'object') {
			if (Array.isArray(data.rows)) {
				return data.rows;
			}
			if (Array.isArray(data.data)) {
				return data.data;
			}
		}

		return [];
	}, [data]);

	const rawData = useMemo(() => {
		return sourceRows.map((item, index) => normalizeDataPoint(item, index));
	}, [sourceRows]);

	const seriesKeys = useMemo(() => {
		const seen = new Set();
		rawData.forEach((row) => {
			const key = typeof row.series === 'string' ? row.series.trim() : '';
			if (key) seen.add(key);
		});
		return Array.from(seen);
	}, [rawData]);

	const hasSeries = seriesKeys.length > 0;
	const [activeSeries, setActiveSeries] = useState(null);

	useEffect(() => {
		if (!activeSeries) return;
		if (!seriesKeys.includes(activeSeries)) {
			setActiveSeries(null);
		}
	}, [activeSeries, seriesKeys]);

	const chartData = useMemo(() => {
		if (!hasSeries) return rawData;

		const grouped = new Map();
		rawData.forEach((row) => {
			const bucket = grouped.get(row.name) || { name: row.name };
			const key = typeof row.series === 'string' ? row.series.trim() : '';
			if (!key) return;

			const existing = typeof bucket[key] === 'number' ? bucket[key] : 0;
			bucket[key] = existing + row.value;
			grouped.set(row.name, bucket);
		});

		return Array.from(grouped.values());
	}, [hasSeries, rawData]);

	const handleLegendClick = (payload) => {
		const value =
			typeof payload?.value === 'string'
				? payload.value
				: typeof payload?.dataKey === 'string'
					? payload.dataKey
					: '';

		if (!value) return;
		setActiveSeries((prev) => (prev === value ? null : value));
	};

	const legendFormatter = (value) => {
		const isInactive = Boolean(activeSeries) && value !== activeSeries;
		return (
			<span style={{ opacity: isInactive ? 0.45 : 1, textDecoration: isInactive ? 'line-through' : 'none' }}>
				{value}
			</span>
		);
	};

	if (!rawData.length) {
		return (
			<div className="flex h-full w-full items-center justify-center rounded-xl bg-slate-50 px-3 text-[12px] text-slate-500">
				No Rows Returned
			</div>
		);
	}

	return (
		<div
			onFocusCapture={(event) => {
				try {
					event.target.blur();
				} catch (error) {
					void error;
				}
			}}
			style={{ height: '100%', width: '100%' }}
		>
			<ResponsiveContainer width="100%" height="100%">
				<LineChart data={chartData} margin={{ top: 10, right: 5, left: 5, bottom: 10 }}>
					<defs>
						<linearGradient id="lineAreaFill" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="rgba(79,111,196,0.28)" />
							<stop offset="100%" stopColor="rgba(79,111,196,0.04)" />
						</linearGradient>
					</defs>
					<CartesianGrid stroke="rgba(148,163,184,0.24)" strokeDasharray="3 4" vertical={false} />
					<XAxis
						dataKey="name"
						stroke="rgba(148,163,184,0.5)"
						tick={{ fill: 'rgba(51,65,85,0.82)', fontSize: 11 }}
						axisLine={false}
						tickLine={false}
					/>
					<YAxis
						stroke="rgba(148,163,184,0.5)"
						tick={{ fill: 'rgba(51,65,85,0.82)', fontSize: 11 }}
						axisLine={false}
						tickLine={false}
						width={26}
					/>
					<Tooltip
						wrapperStyle={{ backgroundColor: '#ffffff', borderRadius: 8, boxShadow: '0 6px 18px rgba(2,6,23,0.08)' }}
						labelStyle={{ color: '#64748b', fontSize: 12 }}
						itemStyle={{ color: '#0f172a' }}
					/>

					{hasSeries ? (
						<Legend verticalAlign="top" wrapperStyle={{ paddingBottom: 6 }} onClick={handleLegendClick} formatter={legendFormatter} />
					) : showSeries ? (
						<Legend verticalAlign="top" wrapperStyle={{ paddingBottom: 6 }} />
					) : null}

					{hasSeries ? (
						seriesKeys.map((seriesName, index) => {
							const color = SERIES_COLORS[index % SERIES_COLORS.length];
							const isVisible = !activeSeries || activeSeries === seriesName;
							return (
								<Line
									key={seriesName}
									type="monotone"
									dataKey={seriesName}
									name={seriesName}
									stroke={color}
									strokeWidth={2.5}
									strokeOpacity={isVisible ? 1 : 0}
									dot={isVisible ? { r: 3.5, fill: '#EAF0FB', stroke: color, strokeWidth: 2 } : false}
									activeDot={isVisible ? { r: 5, fill: '#FFFFFF', stroke: color, strokeWidth: 2 } : false}
									connectNulls
								/>
							);
						})
					) : (
						<>
							<Area type="monotone" dataKey="value" stroke="none" fill="url(#lineAreaFill)" />
							<Line
								type="monotone"
								dataKey="value"
								stroke="#4F6FC4"
								strokeWidth={2.5}
								dot={{ r: 4, fill: '#EAF0FB', stroke: '#4F6FC4', strokeWidth: 2 }}
								activeDot={{ r: 5, fill: '#FFFFFF', stroke: '#4F6FC4', strokeWidth: 2 }}
							/>
						</>
					)}
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
}

export default LineChartWidget;
